import { PublicClientApplication, Configuration, AccountInfo } from '@azure/msal-browser';
import {
  msalDomainHint,
  msalLoginScopes,
  preferredEmailDomain,
  preferredLoginHint,
} from '../store/authConfig';

/** True when the app runs inside another frame (e.g. Salesforce Canvas). MSAL redirect flows are not allowed here. */
export function isAuthEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

class AuthService {
  private msalInstance: PublicClientApplication;

  constructor(config: Configuration) {
    this.msalInstance = new PublicClientApplication(config);
  }

  private getKnownLoginHint(): string | undefined {
    if (preferredLoginHint) {
      return preferredLoginHint;
    }
    const all = this.msalInstance.getAllAccounts();
    const preferred = all.find((a) =>
      a.username?.toLowerCase().endsWith(`@${preferredEmailDomain.toLowerCase()}`)
    );
    if (preferred?.username) {
      return preferred.username;
    }
    const active = this.msalInstance.getActiveAccount();
    if (active?.username) {
      return active.username;
    }
    return all[0]?.username;
  }

  private setPreferredActiveAccount(accounts: AccountInfo[]): void {
    const preferred = accounts.find((a) =>
      a.username?.toLowerCase().endsWith(`@${preferredEmailDomain.toLowerCase()}`)
    );
    this.msalInstance.setActiveAccount(preferred ?? accounts[0]);
  }

  /**
   * Handles redirect round-trip on top-level window; in iframes skips redirect handling
   * (MSAL throws redirect_in_iframe) and falls back to cached accounts / silent SSO.
   */
  async handleRedirect(): Promise<AccountInfo[] | null> {
    const embedded = isAuthEmbedded();

    if (!embedded) {
      try {
        const response = await this.msalInstance.handleRedirectPromise();
        if (response?.account) {
          return this.msalInstance.getAllAccounts();
        }
      } catch (error) {
        console.error(`Error during authentication: ${error}`);
      }
    }

    const existing = this.msalInstance.getAllAccounts();
    if (existing.length > 0) {
      this.setPreferredActiveAccount(existing);
      return existing;
    }

    if (embedded) {
      try {
        await this.msalInstance.ssoSilent({
          scopes: [...msalLoginScopes],
          loginHint: this.getKnownLoginHint(),
          domainHint: msalDomainHint,
        });
        const afterSilent = this.msalInstance.getAllAccounts();
        if (afterSilent.length > 0) {
          this.setPreferredActiveAccount(afterSilent);
        }
        return afterSilent.length > 0 ? afterSilent : null;
      } catch {
        // Third-party cookies / no session — caller should use loginPopup
        return null;
      }
    }

    return null;
  }

  getMsalInstance(): PublicClientApplication {
    return this.msalInstance;
  }
}

export default AuthService;